import { useEffect, useState, useRef } from 'react';
import { DailyProvider, useDaily, useLocalSessionId, useParticipant, useAudioTrack, useVideoTrack } from '@daily-co/daily-react';
import DailyIframe from '@daily-co/daily-js';
import './DailyRoom.css';

interface DailyRoomProps {
  url: string;
  token: string;
  onLeave?: () => void;
}

const Participant: React.FC<{ participantId: string }> = ({ participantId }) => {
  const participant = useParticipant(participantId);
  const { state: audioState, track: audioTrack } = useAudioTrack(participantId);
  const audioElement = useRef<HTMLAudioElement>(null);
  
  useEffect(() => {
    if (audioTrack && audioElement.current) {
      audioElement.current.srcObject = new MediaStream([audioTrack]);
    }
  }, [audioTrack]);

  return (
    <div className="participant-card">
      <div className="participant-avatar">
        {participant?.user_name?.charAt(0) || 'A'}
      </div>
      <div className="participant-info">
        <h4>{participant?.user_name || 'Anonymous'}</h4>
        <div className="participant-status">
          <span className={`status-indicator ${audioState === 'playable' ? 'active' : 'inactive'}`}>
            {audioState === 'playable' ? '🎤' : '🔇'}
          </span>
        </div>
      </div>
      <audio ref={audioElement} autoPlay playsInline />
    </div>
  );
};

const DailyRoomInner: React.FC = () => {
  const daily = useDaily();
  const localSessionId = useLocalSessionId();
  const localParticipant = useParticipant(localSessionId);
  const [participants, setParticipants] = useState<Record<string, any>>({});
  const [audioEnabled, setAudioEnabled] = useState(true);
  
  useEffect(() => {
    if (!daily) return;
    
    const updateParticipants = () => {
      setParticipants(daily.participants());
    };
    
    updateParticipants();
    
    daily.on('participant-joined', updateParticipants);
    daily.on('participant-updated', updateParticipants);
    daily.on('participant-left', updateParticipants);
    
    return () => {
      daily.off('participant-joined', updateParticipants);
      daily.off('participant-updated', updateParticipants);
      daily.off('participant-left', updateParticipants);
    };
  }, [daily]);
  
  useEffect(() => {
    console.log('Daily room status:', daily?.meetingState());
    if (daily?.meetingState() === 'joined-meeting') {
      daily.setLocalAudio(true);
    }
  }, [daily?.meetingState()]);

  const handleLeave = async () => {
    await daily?.leave();
  };
  
  const toggleAudio = () => {
    if (daily) {
      const newState = !audioEnabled;
      daily.setLocalAudio(newState);
      setAudioEnabled(newState);
    }
  };

  return (
    <div className="daily-room-container">
      <div className="room-header">
        <h2>Active Meeting</h2>
        {localParticipant && (
          <div className="user-info">
            <span className="user-name">Connected as: {localParticipant.user_name || 'Anonymous'}</span>
          </div>
        )}
      </div>

      <div className="room-content">
        <div className="participants-section">
          <div className="participants-header">
            <h3>Participants ({Object.keys(participants).length})</h3>
          </div>
          <div className="participants-list">
            {Object.values(participants)
              .filter((p: any) => p.session_id !== localSessionId)
              .map((participant: any) => (
                <Participant key={participant.session_id} participantId={participant.session_id} />
              ))}
            {Object.values(participants).filter((p: any) => p.session_id !== localSessionId).length === 0 && (
              <div className="no-participants">No other participants yet</div>
            )}
          </div>
        </div>
      </div>
      
      <div className="room-controls">
        <button 
          className={`control-button ${audioEnabled ? 'active' : 'inactive'}`}
          onClick={toggleAudio}
        >
          {audioEnabled ? '🎤 Mute' : '🔇 Unmute'}
        </button>
        
        <button 
          className="leave-button"
          onClick={handleLeave}
        >
          Exit Meeting
        </button>
      </div>
    </div>
  );
};

const DailyRoom: React.FC<DailyRoomProps> = ({ url, token, onLeave }) => {
  const [callObject, setCallObject] = useState<any>(null);
  const hasJoinedRef = useRef(false);
  const userInitiatedLeaveRef = useRef(false);

  useEffect(() => {
    console.log("DailyRoom mounted with URL:", url);   // Initialize Daily call object using the imported module
    if (!callObject) {
      const newCallObject = DailyIframe.createCallObject({
        audioSource: true,
        startAudioOff: true,
        startVideoOff: true,
        videoSource: false,
      });
      
      newCallObject.on('left-meeting', (event: any) => {
        console.log('Left meeting event triggered', event);
        if (userInitiatedLeaveRef.current || hasJoinedRef.current) {
          onLeave?.();
        }
      });
      
      setCallObject(newCallObject);
    }

    const joinRoom = async () => {
      if (!callObject) return;
      
      try {
        console.log("Attempting to join room:", url);
        await callObject.join({
          url: url,
          token: token,
          startAudioOff: false,
          audioSource: true,
        });
        
        console.log("Successfully joined room");
        hasJoinedRef.current = true;
        
        await callObject.setLocalAudio(true);
        await callObject.setLocalVideo(false);
      } catch (error) {
        console.error('Error joining Daily room:', error);
      }
    };

    if (callObject && url && token) {
      joinRoom();
    }

    return () => {
      console.log("DailyRoom component unmounting");
      const cleanup = async () => {
        try {
          if (callObject) {
            if (hasJoinedRef.current) {
              userInitiatedLeaveRef.current = true;
              await callObject.leave();
            }
            callObject.destroy();
          }
        } catch (error) {
          console.error('Error cleaning up Daily room:', error);
        }
      };
      cleanup();
    };
  }, [callObject, url, token, onLeave]);

  if (!callObject) return <div>Loading...</div>;

  return (
    <DailyProvider callObject={callObject}>
      <DailyRoomInner />
    </DailyProvider>
  );
};

export default DailyRoom; 